/* eslint-disable react/react-in-jsx-scope */
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import { dateFormat, dateFormatShort } from "model/schema";
import { forwardRef, useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { MoreTime } from "@mui/icons-material";
import {
  isDateValid,
  toDate,
  FORMAT_DATE_TIME_ISO,
  FORMAT_ISO,
  FORMAT_DATE_PARTIEL,
  FORMAT_DATE_PARTIEL_PLACEHOLDER,
  FORMAT_DATE_PLACEHOLDER,
  safeIsEqual,
} from "utils/toDate";
import { FieldMetadata, useInputControl } from "@conform-to/react";
import { ErrorParagraph } from "components/ErrorParagraph";
import { useDidUpdateEffect } from "utils/useDidUpdateEffect";

type DateFieldProps = DatePickerProps<any> & {
  name?: string;
  error?: boolean;
  helperText?: string[];
  required?: boolean;
  fullWidth?: boolean;
  switchFormat?: boolean;
  hideDisplayFormat?: boolean;
  onSwitchFormat?: () => void;
  defaultSwitchedFormat?: boolean;
  minDate?: Date;
  maxDate?: Date;
  onChange?: (e: any) => void;
  field: FieldMetadata<any>;
  tableHeadStyle?: boolean;
  width?: string;
  blurOnClear?: boolean;
};

const DateStyled = forwardRef<HTMLInputElement, DateFieldProps>(function DateStyled(
  { field, onChange, tableHeadStyle, ...props },
  ref,
) {
  const width = props.fullWidth || props.switchFormat ? "100%" : (props.width ?? "300px");
  const minDate = props.minDate ? new Date(props.minDate.setHours(0, 0, 0, 0)) : undefined;
  const maxDate = props.maxDate ? new Date(props.maxDate.setHours(0, 0, 0, 0)) : undefined;
  const shouldDisableMinDate = (date: Date) => {
    return (minDate && date < minDate) ?? false;
  };

  const shouldDisableMaxDate = (date: Date) => {
    return (maxDate && date > maxDate) ?? false;
  };

  const shouldDisableDate = (date: Date) => {
    return shouldDisableMinDate(date) || shouldDisableMaxDate(date);
  };

  // Valeur contrôllée
  const [value, setValue] = useState<Date | null>(() => getDefaultDate(field.initialValue as any));

  // Valeur controllée qui servira à l'éventuel onChange fourni en paramètre
  const [valueForPropsOnChange, setValueForPropsOnChange] = useState<Date | null | undefined>(
    () => undefined,
  );
  useEffect(() => {
    if (onChange && valueForPropsOnChange !== undefined) {
      onChange(valueForPropsOnChange);
    }
  }, [valueForPropsOnChange, onChange]);

  // Lier la valeur du modele au composant
  const date = useInputControl(field as any);
  const inputRef = ref as React.MutableRefObject<HTMLInputElement | null>;
  const datePickerInputRef = useRef<HTMLInputElement>(null);

  // errors géré de cette manière pour s'assurer qu'ils sont mis à jour
  const [errors, setErrors] = useState(Array.isArray(field.errors) ? field.errors : []);
  useEffect(() => {
    setErrors(Array.isArray(field.errors) ? field.errors : []);
  }, [field]);

  // Trigger seulement après sélection avec le datePicker
  useDidUpdateEffect(() => {
    // Déclencher validation de l'input pour mettre à jour les erreurs
    if (inputRef !== null) {
      inputRef?.current?.focus();
    } else {
      datePickerInputRef.current?.focus();
    }
  }, [value, inputRef, datePickerInputRef]);

  const blurInputAfterClear = () => {
    const el = inputRef?.current ?? datePickerInputRef.current;
    if (!el) return;

    // force un vrai focusOut pour déclencher la validation onBlur
    requestAnimationFrame(() => {
      el.focus();
      el.blur();
    });
  };

  return (
    <Box sx={{ position: "relative", width: props.fullWidth ? "100%" : "unset" }}>
      <DatePicker
        format={dateFormat}
        inputRef={inputRef ?? datePickerInputRef}
        value={value}
        // Lorsque que la valeur est sélectionné dans le calendrier
        onAccept={(v) => {
          setValue(v); // met à jour l’état local immédiatement
          date.change(v as any); // met à jour le modèle Conform
        }}
        localeText={{
          fieldDayPlaceholder: () => "JJ",
          fieldMonthPlaceholder: () => "MM",
          fieldYearPlaceholder: () => "AAAA",
          clearButtonLabel: "Effacer",
        }}
        slotProps={{
          actionBar: {
            actions: ["clear"],
          },
          field: {
            clearable: true,
            onClear: (e) => {
              if (props.blurOnClear) {
                blurInputAfterClear();
              }
            },
          },
          textField: {
            FormHelperTextProps: { style: { fontWeight: "bold" } },
            name: props.name,
            variant: "filled",
            size: "small",
            error: errors.length > 0,
            helperText: errors.map((text) => (
              <ErrorParagraph error={text} key={text} inputFieldError />
            )),
            required: props.required,
            fullWidth: props.fullWidth,
            //      InputProps: { endAdornment: <img src="/icon-date.svg" alt="icon date" /> },
            sx: {
              width: props.fullWidth ? "100%" : width,

              ...(tableHeadStyle && {
                "& .MuiFilledInput-root": {
                  backgroundColor: "#00103aff !important",
                  borderRadius: "20px !important",
                  paddingTop: 0,
                },

                "& .MuiFilledInput-root:hover": {
                  backgroundColor: "primary.dark",
                },

                "& .MuiFilledInput-input": {
                  fontSize: 12,
                  color: "white",
                },

                "& .MuiInputLabel-root": {
                  color: "white !important",
                },

                "& .MuiInputLabel-root.Mui-focused": {
                  color: "white",
                },
                "& .MuiSvgIcon-root": {
                  color: "white",
                },
                "& .MuiFilledInput-root::before": {
                  borderBottom: "none !important",
                },
              }),
            },
            // Lorsque la valeur est saisie ou clear
            onChange: (e) => {
              // Mets à jour automatiquement la valeur du modèle, sauf qu'elle est vide -> le faire manuellement
              if (e === null) {
                date.change(undefined);
              }
            },
            onBlur: (e) => {
              e.preventDefault();
              // Lancer le onChange fournit en paramètre lorsque l'utilisateur enlève le focus du champ texte (et donc a fini d'écrire)
              if (onChange) {
                const v = inputRef ? inputRef?.current?.value : datePickerInputRef?.current?.value;
                const vDate = toDate(v);
                if (
                  valueForPropsOnChange === undefined ||
                  !safeIsEqual(valueForPropsOnChange, vDate)
                ) {
                  setValueForPropsOnChange(vDate);
                }
              }
            },
          },
        }}
        {...props}
        shouldDisableDate={
          maxDate && minDate
            ? shouldDisableDate
            : minDate
              ? shouldDisableMinDate
              : maxDate
                ? shouldDisableMaxDate
                : undefined
        }
      />
      {!props.hideDisplayFormat && (
        <Box sx={{ position: "absolute", right: 0, mt: "5px" }}>
          <Typography
            fontSize={11}
            color={props.disabled ? "rgba(0, 0, 0, 0.38)" : tableHeadStyle ? "white" : "primary"}
          >
            {FORMAT_DATE_PARTIEL === props.format
              ? FORMAT_DATE_PARTIEL_PLACEHOLDER
              : FORMAT_DATE_PLACEHOLDER}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

export const DateField = forwardRef<HTMLInputElement, DateFieldProps>(function DateField(
  { label, field, ...props },
  ref,
) {
  const { switchFormat, onSwitchFormat, defaultSwitchedFormat } = props;
  const [toggleFormat, setToggle] = useState(defaultSwitchedFormat ?? false);

  useEffect(() => {
    setToggle(defaultSwitchedFormat ?? false);
  }, [defaultSwitchedFormat]);

  if (switchFormat)
    return (
      <Box sx={{ position: "relative", width: props.fullWidth ? "100%" : "300px" }}>
        <Box sx={{ width: "calc(100% - 30px)" }}>
          <DateStyled
            field={field}
            ref={ref}
            label={label}
            format={toggleFormat ? dateFormatShort : dateFormat}
            views={toggleFormat ? ["month", "year"] : undefined}
            {...props}
          />
        </Box>
        {switchFormat && (
          <Box sx={{ position: "absolute", right: "-8px", top: "5px" }}>
            <IconButton
              aria-label="toggle-format-date"
              color={toggleFormat ? "primary" : undefined}
              onClick={(e) => {
                e.preventDefault();
                setToggle((t) => !t);
                onSwitchFormat?.();
              }}
            >
              <MoreTime color="primary" />
            </IconButton>
          </Box>
        )}
      </Box>
    );

  return <DateStyled field={field} ref={ref} label={label} {...props} />;
});
export const DateConform = forwardRef<HTMLInputElement, DateFieldProps>(function DateConform(
  { label, field, ...props },
  ref,
) {
  return (
    <DateField
      field={field}
      ref={ref}
      label={label}
      name={field.name}
      error={Boolean(field.errors)}
      helperText={field.errors}
      defaultValue={getDefaultDate(field.initialValue as any)}
      key={field.key}
      aria-invalid={!field.valid || undefined}
      aria-describedby={!field.valid ? field.errorId : undefined}
      required={field.required}
      {...props}
    />
  );
});

export function getDefaultDate(str?: string | Date | null) {
  try {
    if (!str) return null;
    if (str instanceof Date) return str;
    const canFormat = toDate(str);
    if (canFormat && isDateValid(canFormat)) return canFormat; // si c'est au format "dd/MM/yyyy"
    const canFormat2 = toDate(str, FORMAT_ISO);
    if (canFormat2 && isDateValid(canFormat2)) return canFormat2; // si c'est au format "yyyy-MM-dd"
    const canFormatDateTime = toDate(str, FORMAT_DATE_TIME_ISO);
    if (canFormatDateTime && isDateValid(canFormatDateTime)) return canFormatDateTime; // si c'est au format date sérialiser ex: 2024-02-19T13:26:06.816Z
    const canFormatDateNaissance = toDate(str, FORMAT_DATE_PARTIEL); // si c'est au format "MM/yyyy"
    if (canFormatDateNaissance && isDateValid(canFormatDateNaissance))
      return canFormatDateNaissance;

    return null;
  } catch (e) {
    return null;
  }
}
